import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Bold, Italic, Underline, Heading2, List, ListOrdered, Link2, Eraser } from "lucide-react";

const cmd = (c, v = null) => document.execCommand(c, false, v);

export const RichEditor = forwardRef(function RichEditor({ value, onChange, onFocus, testid = "rich-editor" }, ref) {
  const areaRef = useRef(null);
  const savedRange = useRef(null);

  useEffect(() => {
    if (areaRef.current && areaRef.current.innerHTML !== (value || "")) {
      areaRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(areaRef.current?.innerHTML || "");

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && areaRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  useImperativeHandle(ref, () => ({
    insertToken(text) {
      const area = areaRef.current;
      if (!area) return;
      area.focus();
      const sel = window.getSelection();
      let range = savedRange.current;
      if (!range || !area.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(area);
        range.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(range);
      const node = document.createTextNode(text);
      range.insertNode(node);
      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
      savedRange.current = range.cloneRange();
      emit();
    },
  }));

  const btn = (icon, action, title, tid) => (
    <button type="button" title={title} data-testid={tid} onMouseDown={(e) => { e.preventDefault(); action(); areaRef.current?.focus(); emit(); }}
      className="h-8 w-8 flex items-center justify-center rounded-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">
      {icon}
    </button>
  );

  const addLink = () => {
    const url = window.prompt("Inserisci URL (https://...)");
    if (url) cmd("createLink", url);
  };

  return (
    <div className="rounded-sm border border-zinc-800 bg-[#0A0A0A] overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-zinc-800 bg-[#141414] px-1.5 py-1">
        {btn(<Bold className="h-4 w-4" />, () => cmd("bold"), "Grassetto", "re-bold")}
        {btn(<Italic className="h-4 w-4" />, () => cmd("italic"), "Corsivo", "re-italic")}
        {btn(<Underline className="h-4 w-4" />, () => cmd("underline"), "Sottolineato", "re-underline")}
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        {btn(<Heading2 className="h-4 w-4" />, () => cmd("formatBlock", "<h2>"), "Titolo", "re-h2")}
        {btn(<List className="h-4 w-4" />, () => cmd("insertUnorderedList"), "Elenco", "re-ul")}
        {btn(<ListOrdered className="h-4 w-4" />, () => cmd("insertOrderedList"), "Elenco numerato", "re-ol")}
        {btn(<Link2 className="h-4 w-4" />, addLink, "Link", "re-link")}
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        {btn(<Eraser className="h-4 w-4" />, () => cmd("removeFormat"), "Rimuovi formattazione", "re-clear")}
      </div>
      <div
        ref={areaRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { saveSelection(); emit(); }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onFocus={() => onFocus && onFocus()}
        data-testid={`${testid}-area`}
        className="min-h-[180px] max-h-[360px] overflow-y-auto px-4 py-3 text-sm text-zinc-100 outline-none prose-invert leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-[#f98125] [&_a]:underline"
      />
    </div>
  );
});
